import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {Project} from '../../domain/types';
import {fetchProjects, createProject, updateProjectThunk, deleteProjectThunk, setCurrentProjectThunk, setDefaultWordlistThunk, loadProjectMetadata} from './projectsThunks';

interface ProjectsState {
    data: Project[];
    currentProject: Project | null;
    loading: boolean;
    error: string | null;
    /**
     * Whether a fetch has settled for the *current* user. `data: []` on its own is ambiguous — it is
     * both "not asked yet" and "this account has none" — and telling those apart is what lets
     * `useOwnedProjectId` refuse to send a project id it cannot vouch for. Reset with the store on
     * sign-out or a user change, so it can never describe the previous account's fetch.
     */
    hasFetched: boolean;
}

const initialState: ProjectsState = {
  data: [],
  currentProject: null,
  loading: false,
  error: null,
  hasFetched: false,
};

// Helper function to sort projects
const sortProjects = (projects: Project[]) => {
  return [...projects].sort((a, b) => {
        // First sort by favorite status
    if (a.favorite !== b.favorite) {
      return a.favorite ? -1 : 1;
    }
        // Then sort by last activity (most recent first)
    const dateA = a.lastActivity ? new Date(a.lastActivity).getTime() : 0;
    const dateB = b.lastActivity ? new Date(b.lastActivity).getTime() : 0;
    if (dateA !== dateB) {
      return dateB - dateA;
    }
        // Fall back to updated date if no lastActivity
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
};

const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    setProjects: (state, action: PayloadAction<Project[]>) => {
      state.data = action.payload;
    },
    setCurrentProject: (state, action: PayloadAction<Project | null>) => {
      state.currentProject = action.payload;
    },
    addProject: (state, action: PayloadAction<Project>) => {
      state.data.push(action.payload);
    },
    updateProject: (state, action: PayloadAction<Project>) => {
      const index = state.data.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.data[index] = action.payload;
      }
    },
    toggleProjectFavorite: (state, action: PayloadAction<string>) => {
      const project = state.data.find(p => p.id === action.payload);
      if (project) {
        project.favorite = !project.favorite;
        state.data = sortProjects(state.data);
      }
    },
    updateProjectLastActivity: (state, action: PayloadAction<string>) => {
      const project = state.data.find(p => p.id === action.payload);
      if (project) {
        project.lastActivity = new Date().toISOString();
      }
    },
    removeProject: (state, action: PayloadAction<string>) => {
      state.data = state.data.filter(p => p.id !== action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
        // Fetch Projects
    builder.addCase(fetchProjects.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchProjects.fulfilled, (state, action) => {
      state.data = sortProjects(action.payload);
      state.loading = false;
      state.error = null;
      state.hasFetched = true;
    });
    builder.addCase(fetchProjects.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      // Settled, even though it failed — otherwise a backend outage leaves every project-scoped
      // screen waiting forever instead of falling back to the unscoped view.
      state.hasFetched = true;
    });

        // Create Project
    builder.addCase(createProject.fulfilled, (state, action) => {
      state.data.push(action.payload);
    });

        // Update Project
    builder.addCase(updateProjectThunk.fulfilled, (state, action) => {
      const index = state.data.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.data[index] = action.payload;
                // Re-sort after update (in case favorite status changed)
        state.data = sortProjects(state.data);
      }
    });

        // Delete Project
    builder.addCase(deleteProjectThunk.fulfilled, (state, action) => {
      state.data = state.data.filter(p => p.id !== action.payload);
      if (state.currentProject?.id === action.payload) {
        state.currentProject = null;
      }
    });

        // Set Current Project (with persistence)
    builder.addCase(setCurrentProjectThunk.fulfilled, (state, action) => {
      state.currentProject = action.payload;
    });

        // Set Default Wordlist
    builder.addCase(setDefaultWordlistThunk.fulfilled, (state, action) => {
      const index = state.data.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.data[index] = action.payload;
      }
            // Also update current project if it's the one being updated
      if (state.currentProject?.id === action.payload.id) {
        state.currentProject = action.payload;
      }
    });

        // Load Project Metadata
    builder.addCase(loadProjectMetadata.fulfilled, (state, action) => {
            // Merge metadata into existing projects
      action.payload.forEach((metadata) => {
        const project = state.data.find(p => p.id === metadata.projectId);
        if (project) {
          project.favorite = metadata.favorite;
          project.lastActivity = metadata.lastActivity || undefined;
        }
      });
            // Re-sort after loading metadata
      state.data = sortProjects(state.data);
    });
  },
});

export const {
  setProjects,
  setCurrentProject,
  addProject,
  updateProject,
  toggleProjectFavorite,
  updateProjectLastActivity,
  removeProject,
  setLoading
} = projectsSlice.actions;
export default projectsSlice.reducer;
